import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calculator, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { ESTIMATOR_RATES, formatINR } from "@/data/site";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TYPES = Object.keys(ESTIMATOR_RATES);
const GRADES = ["Standard", "Premium", "Luxury"];

export default function CostEstimator({ compact = false }) {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [type, setType] = useState("Residential");
  const [sqft, setSqft] = useState("1200");
  const [grade, setGrade] = useState("Standard");
  const [result, setResult] = useState(null);

  const calculate = () => {
    const area = parseInt(sqft, 10);
    if (!area || area <= 0) return;
    const rate = ESTIMATOR_RATES[type][grade];
    const cost = rate * area;
    const months = type === "Structural Plan" ? Math.max(1, Math.ceil(area / 5000)) : Math.max(2, Math.ceil(area / 1200) + 2);
    setResult({ type, grade, area, rate, cost, months });
  };

  const discuss = () => {
    if (!result) return;
    navigate("/contact", {
      state: {
        estimate: `Estimate request — ${result.type} (${result.grade}), ${result.area.toLocaleString(
          "en-IN"
        )} sq.ft, approx ${formatINR(result.cost)}, timeline ~${result.months} months.`,
      },
    });
  };

  return (
    <div
      data-testid="cost-estimator-calculator"
      className="bg-white border border-orange-100 rounded-2xl shadow-xl shadow-orange-500/5 overflow-hidden"
    >
      <div className="h-1.5 bg-gradient-to-r from-orange-500 via-amber-500 to-teal-500" />
      <div className="p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 bg-orange-100 text-orange-600 flex items-center justify-center rounded-2xl">
            <Calculator className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-heading text-xl font-bold text-slate-900">{t("est.title")}</h3>
            <p className="text-[11px] uppercase tracking-widest text-teal-600 font-bold">
              {t("est.subtitle")}
            </p>
          </div>
        </div>
        <div className={`grid gap-4 ${compact ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-3"}`}>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
              {t("est.type")}
            </label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="estimator-type-select" className="rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPES.map((tp) => (
                  <SelectItem key={tp} value={tp} data-testid={`estimator-type-${tp.toLowerCase().replace(/\s+/g, "-")}`}>
                    {t(`types.${tp}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
              {t("est.area")}
            </label>
            <Input
              data-testid="estimator-sqft-input"
              type="number"
              min="50"
              value={sqft}
              onChange={(e) => setSqft(e.target.value)}
              placeholder="e.g. 1200"
              className="rounded-xl focus-visible:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
              {t("est.grade")}
            </label>
            <Select value={grade} onValueChange={setGrade}>
              <SelectTrigger data-testid="estimator-grade-select" className="rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GRADES.map((g) => (
                  <SelectItem key={g} value={g} data-testid={`estimator-grade-${g.toLowerCase()}`}>
                    {t(`grades.${g}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <button
          data-testid="estimator-calculate-button"
          onClick={calculate}
          className="mt-5 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-orange-500 text-white font-semibold px-7 py-3 rounded-full hover:bg-orange-600 transition-colors duration-200 shadow-lg shadow-orange-500/30"
        >
          {t("est.calculate")} <ArrowRight className="w-4 h-4" />
        </button>
        {result && (
          <div data-testid="estimator-result" className="mt-6 border border-orange-200 bg-orange-50 rounded-2xl p-5 animate-fade-up">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center sm:text-left">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{t("est.budget")}</div>
                <div className="font-heading text-2xl font-extrabold text-orange-600" data-testid="estimator-cost">
                  {formatINR(result.cost)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{t("est.rate")}</div>
                <div className="font-heading text-2xl font-extrabold text-teal-700">
                  {formatINR(result.rate)}<span className="text-sm font-semibold text-slate-500">{t("est.perSqft")}</span>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{t("est.timeline")}</div>
                <div className="font-heading text-2xl font-extrabold text-slate-900">
                  ~{result.months} <span className="text-sm font-semibold text-slate-500">{t("est.months")}</span>
                </div>
              </div>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <p className="text-xs text-slate-500">{t("est.disclaimer")}</p>
              <button
                data-testid="estimator-discuss-button"
                onClick={discuss}
                className="inline-flex items-center gap-2 border-2 border-teal-600 text-teal-700 font-semibold px-5 py-2.5 rounded-full hover:bg-teal-600 hover:text-white transition-colors duration-200 text-sm"
              >
                {t("est.discuss")} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
